/** Lazy per-finding Leaflet mini-map (GIS Phase 2A, Task 11a, spec §5/§6).
 *
 *  Fed ONLY the evidence GeoJSON `EvidencePanel` passes in — never the main
 *  map's layer stack (spec §5: "the queue NEVER fetches the main map's
 *  layer stack"). Callers control lifecycle by mount/unmount: this
 *  component is only ever rendered while its finding row is expanded AND
 *  evidence has loaded, so its own mount IS the "instantiate on expand"
 *  event and its own unmount (collapse, or the row going away entirely)
 *  IS the "clean up" event — one `useEffect` cleanup covers both, which is
 *  what makes a leaked map instance per expanded row (the classic Leaflet
 *  bug this task's brief calls out) structurally impossible here.
 *
 *  Uses raw Leaflet (`L.map`/`L.geoJSON`/`L.circleMarker`), not
 *  `react-leaflet`'s declarative `<MapContainer>` — the imperative API is
 *  what makes "map created on expand only" and "`remove()` on collapse" a
 *  literal, testable statement rather than an implication of React's own
 *  mount lifecycle wrapped in another abstraction. `MiniMap.test.tsx`
 *  mocks `leaflet` directly (module review I6: jsdom cannot render tiles,
 *  and no Leaflet test precedent exists in the repo to follow instead).
 *
 *  Marker roles come from `services/gis_evidence.py`'s GeoJSON feature
 *  tagging (`properties.role`): `"asset"` (the finding's own entity),
 *  `"candidate"` (a candidate parent), `"conductor"` (a nearby conductor
 *  segment, rendered as a line, not a marker). Asset vs. candidate markers
 *  are styled distinctly; a candidate marker click forwards its
 *  `entity_id` via `onSelectCandidate` (the same value the evidence
 *  panel's candidate-table radio selects), and selecting a candidate row
 *  restyles its marker in place — no map/layer rebuild — so the two
 *  surfaces visually correspond without over-building a two-way sync. */

import { useEffect, useRef } from 'react'
import L from 'leaflet'

const ROLE_COLOR: Record<string, string> = {
  asset: '#111827',
  candidate: '#2563eb',
  conductor: '#f97316',
}

const SELECTED_COLOR = '#f59e0b'

/** Same tile source/attribution as every other Leaflet surface in this
 *  codebase (`frontend/src/components/map/MapView.tsx`,
 *  `frontend/src/components/oms/OutageMap.tsx`, both via react-leaflet's
 *  `<TileLayer>`) — kept as raw-Leaflet constants here since MiniMap uses
 *  `L.map`/`L.geoJSON` directly, not `react-leaflet` (see module docstring). */
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'

interface EvidenceFeatureProperties {
  role?: string
  entity_id?: number
}

function propertiesOf(feature: GeoJSON.Feature | undefined): EvidenceFeatureProperties {
  return (feature?.properties ?? {}) as EvidenceFeatureProperties
}

function featureOf(layer: L.Layer): GeoJSON.Feature | undefined {
  return (layer as L.Layer & { feature?: GeoJSON.Feature }).feature
}

interface MiniMapProps {
  /** The evidence endpoint's own `geojson` field — fed verbatim, never
   *  merged with any other layer. */
  geojson: Record<string, unknown>
  /** The candidate `entity_id` currently selected in the evidence panel's
   *  table (relationship findings only); highlights the matching marker.
   *  Undefined/null for finding types with no candidates. */
  selectedCandidateId?: number | null
  /** Fired when a candidate marker is clicked, so the mini-map and the
   *  candidate table can drive the same selection either direction. */
  onSelectCandidate?: (entityId: number) => void
}

export function MiniMap({ geojson, selectedCandidateId = null, onSelectCandidate }: MiniMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const layerRef = useRef<L.GeoJSON | null>(null)

  // Map + layer lifecycle: created once per mount (= once per expand, since
  // a row's `geojson` prop is the result of one lazy fetch and does not
  // change while the row stays expanded), destroyed on unmount (= collapse
  // or the row leaving the DOM entirely).
  useEffect(() => {
    if (!containerRef.current) return

    const map = L.map(containerRef.current, { attributionControl: false })

    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION }).addTo(map)

    const layer = L.geoJSON(geojson as unknown as GeoJSON.GeoJsonObject, {
      pointToLayer: (feature, latlng) => {
        const props = propertiesOf(feature)
        const role = props.role ?? 'asset'
        // Consult the CURRENT `selectedCandidateId` (the prop in scope for
        // whichever render last changed `geojson`, since this effect closure
        // is recreated every render and React only invokes the newest one
        // when deps change) so a map rebuilt by a background refetch (new
        // `geojson` object identity, e.g. react-query's window-focus
        // refetch) still paints the active selection instead of losing it
        // until the operator re-clicks a candidate (L1, task-11a review).
        const isSelected =
          role === 'candidate' && selectedCandidateId != null && props.entity_id === selectedCandidateId
        return L.circleMarker(latlng, {
          radius: role === 'asset' ? 7 : 5,
          color: isSelected ? SELECTED_COLOR : ROLE_COLOR[role] ?? '#6b7280',
          fillColor: isSelected ? SELECTED_COLOR : ROLE_COLOR[role] ?? '#6b7280',
          fillOpacity: 0.85,
          weight: isSelected ? 3 : 1.5,
        })
      },
      style: feature => {
        const role = propertiesOf(feature).role
        return {
          color: (role && ROLE_COLOR[role]) ?? '#6b7280',
          weight: role === 'conductor' ? 2 : 1,
        }
      },
      onEachFeature: (feature, layer) => {
        const props = propertiesOf(feature)
        if (props.role === 'candidate' && props.entity_id !== undefined && onSelectCandidate) {
          layer.on('click', () => onSelectCandidate(props.entity_id as number))
        }
      },
    }).addTo(map)

    layerRef.current = layer

    const bounds = layer.getBounds()
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 18 })
    } else {
      map.setView([0, 0], 1)
    }

    return () => {
      map.remove()
      layerRef.current = null
    }
    // Deliberately keyed on `geojson` alone (not `selectedCandidateId`, read
    // inside `pointToLayer` above only to seed a rebuilt map's initial
    // styling — ongoing selection changes are handled below without tearing
    // the map down) — see module docstring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson])

  // Restyle in place on selection change — never rebuilds the map/layer.
  useEffect(() => {
    const layer = layerRef.current
    if (!layer) return
    layer.eachLayer(child => {
      const props = propertiesOf(featureOf(child))
      if (props.role !== 'candidate') return
      const isSelected = selectedCandidateId != null && props.entity_id === selectedCandidateId
      if ('setStyle' in child) {
        ;(child as L.CircleMarker).setStyle({
          color: isSelected ? SELECTED_COLOR : ROLE_COLOR.candidate,
          fillColor: isSelected ? SELECTED_COLOR : ROLE_COLOR.candidate,
          weight: isSelected ? 3 : 1.5,
        })
      }
    })
  }, [selectedCandidateId])

  return (
    <div
      ref={containerRef}
      data-testid="mini-map"
      style={{ height: 220, width: '100%', borderRadius: 8 }}
    />
  )
}
